import { useEffect, useMemo, useRef, useState } from "react";
import { MARKETS } from "../data/catalogue";
import type { GeneratedImage, ImagePlanSlot, MarketId, Platform, ReferenceRole, WorkspaceStage, WorkspaceState } from "../types/workspace";
import { compilePlanForPlatform } from "../core/planCompiler";
import { localizePurposeForLocale } from "../i18n";

import { callShaper } from "../core/shaper";
import { generateImages as runGeneration } from "../core/generationClient";
import { type PreparedPlan } from "../core/promptCompiler";
import { exportBatchZip, listBatches, saveBatch, type BatchRecord } from "../core/batchStore";
import { logActivity } from "../core/activityLogger";

const palettes = ["stone", "amber", "coral", "ink", "mint", "copper", "sand", "lavender", "charcoal", "pearl"];

const clonePlan = (platform: Platform, imageCount = 10) => compilePlanForPlatform(platform, imageCount).map((slot) => ({ ...slot, preserve: { ...slot.preserve } }));

export function useWorkspace(authenticated = false) {
  const defaultMarket = MARKETS.find((market) => market.id === "jp")!;
  const [state, setState] = useState<WorkspaceState>({
    stage: "setup",
    locale: (window.localStorage.getItem("picasso-locale") as WorkspaceState["locale"]) || "zh-Hant",
    market: defaultMarket,
    platform: defaultMarket.platforms[0],
    productReady: false,
    productName: "",
    confirmedFacts: [],
    referenceImages: [],
    plan: clonePlan(defaultMarket.platforms[0]),
    selectedSlotId: "slot-03",
    creativeDirection: "",
    images: []
  });

  useEffect(() => { window.localStorage.setItem("picasso-locale", state.locale); }, [state.locale]);

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<PreparedPlan | null>(null);
  const [history, setHistory] = useState<BatchRecord[]>([]);
  const lock = useRef(false);
  const batchRef = useRef<BatchRecord | null>(null);
  useEffect(() => { listBatches().then(setHistory).catch(error => setNotice(String(error))); }, []);
  const persist = async (batch:BatchRecord) => {
    batchRef.current = batch;
    try { await saveBatch(batch); setHistory(await listBatches()); }
    catch (error) { setNotice(`Images remain available in this tab; saving failed: ${String(error)}`); }
  };
  const prepare = async () => {
    const resolved = preview && JSON.stringify(preview.workspace.plan) === JSON.stringify(state.plan)
      && preview.workspace.productName === state.productName && preview.workspace.creativeDirection === state.creativeDirection
      && preview.workspace.platform.id === state.platform.id && preview.workspace.productImageUrl === state.productImageUrl
      ? preview : await callShaper(state);
    setPreview(resolved); if (resolved.warning) setNotice(`Using platform fallback: ${resolved.warning}`);
    return resolved;
  };
  const previewPrompts = async () => {
    if (!authenticated) { setNotice("Please log in to generate images."); return; }
    if(lock.current) return; lock.current=true;setBusy(true);setNotice("");
    try { await prepare(); } catch(error){setNotice(String(error));}
    finally {lock.current=false;setBusy(false);}
  };
  const generate = async (isolated = false) => {
    if (!authenticated) { setNotice("Please log in to generate images."); return; }
    if(lock.current) return;
    lock.current=true;setBusy(true);setNotice("");setProgress(0);
    try {
      const existing = batchRef.current;
      const prepared = isolated && existing ? existing.prepared : await prepare();
      const selected = state.images.find(image=>image.slotId===state.selectedSlotId);
      const targetId = state.selectedSlotId;
      const prompts = isolated ? prepared.prompts.filter(prompt=>prompt.slotId===targetId).map(prompt=>({
        ...prompt,id:`prompt-${crypto.randomUUID()}`,prompt:prompt.prompt + `\n\nISOLATED CHANGE REQUEST\n${selected?.request || ''}\n${selected?.quickAdjustments.join('; ') || ''}\nPreserve product identity and platform rules.`
      })) : prepared.prompts;
      if(!prompts.length) throw new Error('No image selected.');
      void logActivity('generation', 'batch_started', { slotCount: prompts.length, isolated });
      const images:GeneratedImage[] = isolated ? state.images.map(image=>image.slotId===targetId ? {...image,status:'queued',approved:false,error:undefined} : image) : prompts.map((prompt,index)=>({slotId:prompt.slotId,version:0,status:'queued',approved:false,palette:palettes[index%palettes.length],request:'',quickAdjustments:[]}));
      const batch:BatchRecord = isolated && existing ? {...existing,status:'in-progress',prepared:{...prepared,prompts:prepared.prompts.map(prompt=>prompts.find(p=>p.slotId===prompt.slotId)||prompt)}} : {id:`batch-${crypto.randomUUID()}`,timestamp:Date.now(),workspace:prepared.workspace,prepared,results:[],status:'in-progress'};
      setState(current=>({...current,stage:'review',selectedSlotId:isolated?targetId:prompts[0].slotId,images}));
      // Store compiled prompts and source assets before any paid request.
      await persist(batch);
      await runGeneration(prompts,prepared,async event=>{
        setProgress(Math.round(event.completed/event.total*100));
        setState(current=>({...current,images:current.images.map(image=>{
          const result=event.results.find(result=>result.slotId===image.slotId);
          if(result) return {...image,status:result.success?'ready':'failed',imageUrl:result.imageUrl || image.imageUrl,error:result.error,version:(images.find(old=>old.slotId===image.slotId)?.version || 0)+(result.success?1:0),approved:false};
          return event.currentSlots.includes(image.slotId)?{...image,status:'generating'}:image;
        })}));
        batch.results=[...batch.results.filter(result=>!event.results.some(next=>next.slotId===result.slotId)),...event.results];
        await persist({...batch});
      });
      batch.status=batch.results.every(result=>result.success)?'complete':'failed'; await persist(batch);
      void logActivity('generation', 'batch_completed', { total: batch.results.length, succeeded: batch.results.filter(result => result.success).length, failed: batch.results.filter(result => !result.success).length });
    } catch(error){ void logActivity('error', 'generation_error', { error: String(error) }); setNotice(String(error)); }
    finally {lock.current=false;setBusy(false);}
  };

  const activeSlots = useMemo(() => state.plan.filter((slot) => slot.enabled), [state.plan]);

  const update = (updater: (current: WorkspaceState) => WorkspaceState) => setState(updater);

  return {
    state: { ...state, activeSlots },
    authenticated, busy, notice, progress, preview, history, previewPrompts,
    loadHistory: (batch:BatchRecord) => {
      if(lock.current) return;
      batchRef.current=batch;setPreview(batch.prepared);
      setState({...batch.workspace,referenceImages:batch.workspace.referenceImages || [],locale:state.locale,stage:'review',selectedSlotId:batch.prepared.prompts[0]?.slotId || '',images:batch.prepared.prompts.map((prompt,index)=>{
        const result=batch.results.find(result=>result.slotId===prompt.slotId);
        return {slotId:prompt.slotId,version:result?.success?1:0,status:result?.success?'ready':'failed',approved:false,palette:palettes[index%palettes.length],request:'',quickAdjustments:[],imageUrl:result?.imageUrl,error:result?.error || (!result?'Generation interrupted; retry this image.':undefined)};
      })});
    },
    exportCurrent: async () => {
      if(!batchRef.current)return;
      const url=URL.createObjectURL(exportBatchZip(batchRef.current));
      const a=document.createElement('a');a.href=url;a.download=`${batchRef.current.id}.zip`;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);
    },
    selectMarket: (marketId: MarketId) => update((current) => {
      const market = MARKETS.find((item) => item.id === marketId)!;
      return { ...current, market, platform: market.platforms[0], plan: clonePlan(market.platforms[0], current.plan.filter((slot) => slot.enabled).length) };
    }),
    selectPlatform: (platform: Platform) => update((current) => ({ ...current, platform, plan: clonePlan(platform, current.plan.filter((slot) => slot.enabled).length) })),
    setProductReady: (ready: boolean, productImageUrl?: string) => update((current) => ({
      ...current,
      productReady: ready,
      productImageUrl: productImageUrl ?? current.productImageUrl,
      productImageUrls: productImageUrl ? [productImageUrl] : current.productImageUrls,
      productName: ready ? "無線降噪頭戴式耳機" : current.productName
    })),
    setProductPhotos: (productImageUrls: string[]) => update((current) => ({ ...current, productReady: productImageUrls.length > 0, productImageUrl: productImageUrls[0] || current.productImageUrl, productImageUrls })),
    setProductName: (productName: string) => update((current) => ({ ...current, productName })),
    addReferenceImage: (file: File) => {
      if (file.type && !file.type.startsWith("image/")) return;
      if (state.referenceImages.length >= 5) return;
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== "string") return;
        update((latest) => latest.referenceImages.length >= 5 ? latest : ({
          ...latest,
          referenceImages: [...latest.referenceImages, { id: `reference-${crypto.randomUUID()}`, name: file.name, data: reader.result as string, roles: [] }]
        }));
      };
      reader.readAsDataURL(file);
    },
    removeReferenceImage: (index: number) => update((current) => ({ ...current, referenceImages: current.referenceImages.filter((_, itemIndex) => itemIndex !== index) })),
    toggleReferenceRole: (index: number, role: ReferenceRole) => update((current) => ({
      ...current,
      referenceImages: current.referenceImages.map((reference, itemIndex) => itemIndex !== index ? reference : {
        ...reference,
        roles: reference.roles.includes(role) ? reference.roles.filter((item) => item !== role) : [...reference.roles, role]
      })
    })),
    toggleFactConfirmation: (fact: WorkspaceState["confirmedFacts"][number]) => update((current) => ({ ...current, confirmedFacts: current.confirmedFacts.includes(fact) ? current.confirmedFacts.filter((item) => item !== fact) : [...current.confirmedFacts, fact] })),
    setStage: (stage: WorkspaceStage) => update((current) => {
      if (stage === current.stage) return current;
      void logActivity('workspace', 'stage_changed', { from: current.stage, to: stage });
      if (stage !== "plan") return { ...current, stage };
      const plan = current.plan.map((slot) => ({ ...slot, purpose: localizePurposeForLocale(current.locale, slot.purpose) }));
      return {
        ...current,
        stage,
        plan,
        selectedSlotId: plan.find((slot) => slot.enabled)?.id || plan[0]?.id || "",
        planLocale: current.locale
      };
    }),
    setLocale: (locale: WorkspaceState["locale"]) => update((current) => ({ ...current, locale })),
    setImageCount: (imageCount: number) => update((current) => {
      const count = Math.max(1, Math.min(10, imageCount));
      return { ...current, plan: current.plan.map((slot, index) => ({ ...slot, enabled: index < count })) };
    }),
    toggleSlot: (slotId: string) => update((current) => ({
      ...current,
      plan: current.plan.map((slot) => slot.id === slotId ? { ...slot, enabled: !slot.enabled } : slot)
    })),
    restoreAllSlots: () => update((current) => ({ ...current, plan: clonePlan(current.platform) })),
    moveSlot: (slotId: string, offset: number) => update((current) => {
      const currentIndex = current.plan.findIndex((slot) => slot.id === slotId);
      const nextIndex = currentIndex + offset;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= current.plan.length) return current;
      const plan = [...current.plan];
      [plan[currentIndex], plan[nextIndex]] = [plan[nextIndex], plan[currentIndex]];
      return { ...current, plan: plan.map((slot, index) => ({ ...slot, number: index + 1 })) };
    }),
    reorderActiveSlots: (ordered: ImagePlanSlot[]) => update((current) => {
      const active = ordered.map((slot) => slot.id);
      const activeById = new Map(current.plan.filter((slot) => slot.enabled).map((slot) => [slot.id, slot]));
      let cursor = 0;
      const plan = current.plan.map((slot) => slot.enabled ? activeById.get(active[cursor++])! : slot);
      return { ...current, plan: plan.map((slot, index) => ({ ...slot, number: index + 1 })) };
    }),
    selectSlot: (selectedSlotId: string) => update((current) => ({ ...current, selectedSlotId })),
    updateCreativeDirection: (creativeDirection: string) => update((current) => ({ ...current, creativeDirection })),
    updateSlot: (slotId: string, patch: Partial<WorkspaceState["plan"][number]>) => update((current) => ({
      ...current,
      plan: current.plan.map((slot) => slot.id === slotId ? { ...slot, ...patch } : slot)
    })),
    generateImages: () => generate(),
    updateSelectedRequest: (request: string) => update((current) => ({ ...current, images: current.images.map((image) => image.slotId === current.selectedSlotId ? { ...image, request } : image) })),
    addQuickAdjustment: (adjustment: string) => update((current) => ({ ...current, images: current.images.map((image) => image.slotId === current.selectedSlotId ? { ...image, quickAdjustments: image.quickAdjustments.includes(adjustment) ? image.quickAdjustments.filter((item) => item !== adjustment) : [...image.quickAdjustments, adjustment] } : image) })),
    regenerateSelected: () => generate(true),
    approveSelected: () => update((current) => ({
      ...current,
      images: current.images.map((image) => image.slotId === current.selectedSlotId ? {
        ...image,
        status: "approved",
        approved: true
      } : image)
    }))
  };
}
